import { LoaderFunctionArgs } from 'react-router';

import { setExternalInvitationCode } from '@/domain/auth/actions/set-external-invitation-code.action';
import { getExternalInfos } from '@/domain/external/actions/get-external-infos.action';
import { ExternalInfos } from '@/domain/external/entities/external.entity';
import { selectExternalInfos } from '@/domain/external/external.selectors';
import { Scope } from '@/domain/external-invitation/entities/external-invitation.entity';
import { getImpactCo2Answers } from '@/domain/impact-co2/actions/get-impact-co2-answers.action';
import { store } from '@/domain/store';
import { translate } from '@/ui/i18n';

/**
 * @param param0 standard params of loader function
 * @param allowedScope extra param to define allowed scope in that route
 * @returns
 */
export async function externalLoader(
  { request }: LoaderFunctionArgs,
  {
    allowedScope,
    loadAnswers = true,
  }: { allowedScope: Scope; loadAnswers?: boolean },
) {
  const url = new URL(request.url);
  const code = url.searchParams.get('code');

  // check code is present
  if (!code) {
    throw new Error(translate('External.Error.code-is-missing'));
  }

  // check that external infos are present
  let externalInfos: ExternalInfos;
  try {
    await store.dispatch(getExternalInfos(code)).unwrap();
    externalInfos = selectExternalInfos(store.getState());
  } catch {
    throw new Error(translate('External.Error.generic-error'));
  }

  if (externalInfos.id === -1) {
    throw new Error(translate('External.Error.generic-error'));
  }

  // check limit date: lock access if status is IN_PROGRESS and limit date is before today
  if (
    externalInfos.status === 'IN_PROGRESS' &&
    externalInfos.expirationDate !== null &&
    new Date(externalInfos.expirationDate) < new Date()
  ) {
    throw new Error(translate('External.Error.code-is-expired'));
  }

  // check that scope is the right one
  if (allowedScope !== externalInfos.scope) {
    throw new Error(translate('External.Error.generic-error'));
  }

  // fetch data
  try {
    const { projectId } = externalInfos;
    await store.dispatch(setExternalInvitationCode(code)).unwrap();
    if (loadAnswers) {
      await store.dispatch(getImpactCo2Answers({ projectId })).unwrap();
    }
  } catch {
    throw new Error(translate('External.Error.generic-error'));
  }

  return null;
}
